import React, { useEffect, useState } from 'react'
import { notify } from 'libs/common/notify'

import { createStripeClient, injectStripeScript, isStripeInjected } from '@elo-kit/utils/stripe.utils'

import { useShopStores } from 'shop/hooks/use-store'

import { ClickSubmitData } from 'shop/types/stripe'
import { CreateOrderResponse } from 'shop/api/orders.api'

import { DEFAULT_STRIPE_AMOUNT, SETUP_FUTURE_USAGE, STRIPE_ELEMENTS_MODE } from 'constants/stripe.constants'
import { PAYMENT_PROVIDERS } from 'constants/paymentSettingShared.constants'

type ExpressSubmitReturnValue = Promise<CreateOrderResponse> | Promise<{ clientSecret: string; redirectLink: string }>

interface Props {
  appleProvider: string
  applePubKey: string
  handleExpressClick: (data: ClickSubmitData) => void
  handleExpressSubmit?: (data?: ClickSubmitData) => ExpressSubmitReturnValue
  handleExpressClose: () => void
  payOptions: {
    amount: number
    currency: string
  }
  show: boolean
}

const APPLE_PAY_EXPRESS_ELEMENT_ID = 'express-apple-checkout-element'

export const ApplePayExpressElement: React.FC<Props> = (props) => {
  const { appleProvider, applePubKey, handleExpressClick, handleExpressSubmit, handleExpressClose, payOptions, show } =
    props

  const [stripeElements, setStripeElements] = useState(null)

  const { paymentStore, purchaseMethodsStore, sellerStore } = useShopStores()

  useEffect(() => {
    if (isStripeInjected()) {
      initAppleExpress()
    } else {
      injectStripeScript(initAppleExpress)
    }
  }, [])

  useEffect(() => {
    if (stripeElements) {
      stripeElements.update({ amount: payOptions.amount || DEFAULT_STRIPE_AMOUNT })
    }
  }, [payOptions.amount])

  const initAppleExpress = () => {
    const options = {
      locale: I18n.locale,
      mode: STRIPE_ELEMENTS_MODE.payment,
      amount: payOptions.amount || DEFAULT_STRIPE_AMOUNT,
      currency: payOptions.currency,
      setupFutureUsage: SETUP_FUTURE_USAGE.offSession,
      ...(appleProvider === PAYMENT_PROVIDERS.elopageConnect
        ? { onBehalfOf: sellerStore.item.elopageConnectAccountId }
        : {}),
    }

    if (applePubKey) {
      const stripeClient = createStripeClient(applePubKey)
      const elements = stripeClient.elements(options)

      setStripeElements(elements)

      const expressCheckoutElement = elements.create('expressCheckout', {
        paymentMethods: {
          googlePay: 'never',
          applePay: 'always',
          link: 'never',
          klarna: 'never',
          amazonPay: 'never',
          paypal: 'never',
        },
      })

      if (expressCheckoutElement) {
        expressCheckoutElement.mount(`#${APPLE_PAY_EXPRESS_ELEMENT_ID}`)
        expressCheckoutElement.on('ready', ({ availablePaymentMethods }) => {
          paymentStore.setAppleAvailability(availablePaymentMethods || {})
          purchaseMethodsStore.setAppleAvailability(availablePaymentMethods || {})
        })

        if (handleExpressClick) {
          expressCheckoutElement.on('click', (event) => {
            const showPopup = () => event.resolve()
            handleExpressClick({
              stripeClient: stripeClient,
              elements: elements,
              expressElement: expressCheckoutElement,
              showPopup: showPopup,
            })
          })
        }

        expressCheckoutElement.on('confirm', async () => {
          const resp = await handleExpressSubmit({ stripeClient: stripeClient })

          if (resp?.clientSecret) {
            const { error } = await stripeClient.confirmPayment({
              elements,
              clientSecret: resp.clientSecret,
              confirmParams: {
                return_url: resp.redirectLink,
              },
            })

            if (error) {
              notify('error', error.message)

              if (handleExpressClose) {
                handleExpressClose()
              }
            }
          }
        })

        expressCheckoutElement.on('cancel', handleExpressClose)
      }
    }
  }

  return <div id={APPLE_PAY_EXPRESS_ELEMENT_ID} style={{ display: show ? 'block' : 'none' }} />
}
